import { useEffect, useState } from "react";

import { Badge } from "@magnetic/badge";
import { Text } from "@magnetic/text";

import { Button } from "src/components/buttons";
import { HFlex } from "src/components/hFlex";
import { VFlex } from "src/components/vFlex";
import { ApiError } from "src/core/apiError";

import { CandidateChangesDrawer } from "./candidateChangesDrawer";
import { BaseCandidate } from "./candidateTypes";
import { CommitCandidateButton } from "./CommitCandidateButton";

import "./previewChanges.scss";

interface PreviewChangesDrawerProps<T extends BaseCandidate> {
  readonly onClose: () => void;
  readonly name?: string;
  readonly fetchCandidates: () => Promise<T[] | undefined>;
  readonly onReviewClick: () => void;
  readonly contextId?: string;
  readonly refreshSignal?: unknown;
}

export const PreviewChangesDrawer = <T extends BaseCandidate>({
  onClose,
  name,
  fetchCandidates,
  onReviewClick,
  contextId,
  refreshSignal
}: PreviewChangesDrawerProps<T>) => {
  const [candidate, setCandidate] = useState<T | undefined>();
  const [candidateLoading, setCandidateLoading] = useState<boolean>(false);
  const [error, setError] = useState<ApiError | undefined>();

  useEffect(() => {
    // This effect owns one candidate request. Cleanup marks it inactive when
    // the drawer closes or a newer refresh starts so an older response cannot
    // overwrite the latest candidate, error, or loading state.
    let active = true;
    /**
     * Loads all the pending transactions.
     * there should just be one with a bunch of
     * events
     */
    if (contextId) {
      setCandidateLoading(true);
      setError(undefined);
      fetchCandidates()
        .then(
          (candidates: T[] = []) => {
            if (active) {
              setCandidate(candidates[0]);
            }
          },
          (err: ApiError) => {
            if (active) {
              setError(err);
            }
          }
        )
        .finally(() => {
          if (active) {
            setCandidateLoading(false);
          }
        });
    }
    return () => {
      active = false;
    };
  }, [contextId, fetchCandidates, refreshSignal]);

  const title = candidate?.events?.length ? (
    <VFlex className="preview-changes-title" gap="xs" justify="center">
      <HFlex gap="xs" align="flex-start">
        <div>Preview changes</div>
        <Badge.Counter
          className="event-count"
          size="sm"
          value={candidate.events.length}
        />
      </HFlex>
      <Text weight="bold">{name}</Text>
    </VFlex>
  ) : (
    "Preview changes"
  );

  return (
    <CandidateChangesDrawer
      title={title}
      onClose={onClose}
      candidate={candidate}
      error={error}
      isOpen={true}
      loading={candidateLoading}
    >
      <Button name="close-drawer" kind="tertiary" onClick={onClose}>
        Close
      </Button>
      <CommitCandidateButton onSuccess={onClose} />
      <Button name="review-config" kind="primary" onClick={onReviewClick}>
        Review configuration
      </Button>
    </CandidateChangesDrawer>
  );
};
